import * as yup from '@if/core/yup'
import { formatCurrency } from '@if/core/utils'
import { Banner, Caption, ControlledSelect, FormContainer, renderSelectMenuItems, useUrlFilters } from '@if/mui'
import { Box, Skeleton, Stack } from '@mui/material'
import { useGetBankJobs, useGetBankJobYears } from '@src/api/lender-payment/bank-jobs-api'
import { useCompany } from '@src/services/company-hooks'
import { getToday } from '@src/services/DateTimeProvider'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import BankJobTable from './BankJobTable'
import { DEFAULT_PERIOD, isRollingPeriod, periodOptions, periodRange, toPeriod } from './disbursement-history-period'

const periodSchema = yup.object({
  period: yup.text().default(DEFAULT_PERIOD),
})

// The "Historique" tab: the deposits that already paid lender payments out, for one period at a
// time. Each file's offices are on its own detail page.
const DisbursementHistory = () => {
  const { t } = useTranslation()
  const financingCompanyId = useCompany()
  // In the URL rather than in state: the detail page's back link goes back in history, so it
  // returns to the period the list was showing.
  const [filters, setFilters] = useUrlFilters(periodSchema)
  const period = toPeriod(filters.period)

  const today = useMemo(() => getToday(), [])
  const range = useMemo(() => periodRange(period, today), [period, today])
  const {
    data: files,
    isLoading,
    isError,
  } = useGetBankJobs({
    financingCompanyId,
    from: range.from.toString(),
    to: range.to?.toString() ?? null,
  })
  const { data: years } = useGetBankJobYears(financingCompanyId)

  const options = useMemo(
    () =>
      renderSelectMenuItems(periodOptions(years ?? []), (option) => ({
        value: option,
        label: isRollingPeriod(option) ? t(`disburse.history.periods.${option}`) : option,
      })),
    [years, t],
  )

  const total = (files ?? []).reduce((sum, file) => sum + file.amount, 0)

  return (
    <Stack spacing={2}>
      {isLoading && <Skeleton variant="rounded" height={260} />}
      {isError && <Banner severity="error">{t('disburse.history.loadError')}</Banner>}
      {files && <BankJobTable files={files} />}

      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="flex-end" gap={2}>
        <Caption>
          {files && files.length > 0
            ? t('disburse.history.summary', { count: files.length, total: formatCurrency(total) })
            : null}
        </Caption>
        <Box sx={{ width: 220 }}>
          <FormContainer
            mode="filters"
            useFormSetup={() => ({ schema: periodSchema, defaultValues: { period } })}
            onSubmit={(values) => setFilters({ period: values.period })}
          >
            {({ control }) => (
              <ControlledSelect
                control={control}
                name="period"
                label={t('disburse.history.period')}
                options={options}
                disableEmptyOption
              />
            )}
          </FormContainer>
        </Box>
      </Stack>
    </Stack>
  )
}

export default DisbursementHistory
